import { type OptionItem } from "@common/controls/Inputs/Select/component";
import { type GenericMap } from "@common/declarations";
import { useNavigation } from "@common/hooks/navigation";
import { parseQueryString } from "@common/lib/url";
import { type Data } from "@common/modules/data/types";
import React from "react";
import BackendProvider from "./Provider/Backend";
import TrafficByUrlProvider, { WEB_TRAFFIC_BY_URL_ID } from "./Provider/Static/TrafficByUrlProvider";
import Report, { type IReport } from "./Report";
import { type ReportData, type ReportGetItemDetail, type ReportSettings, type ReportTitle } from "./types";

import ConversionsByTrialProvider, { CONVERSIONS_BY_TRIAL } from "./Provider/Static/ConversionsByTrialProvider";
import TotalSalesByAccessCardProvider, {
  SHOP_TOTAL_SALES_BY_ACCESS_CARD
} from "./Provider/Static/TotalSalesByAccessCardProvider";
import TotalSalesByContactProvider, {
  SHOP_TOTAL_SALES_BY_CONTACT
} from "./Provider/Static/TotalSalesByContactProvider";
import TotalSalesByCountryProvider, {
  SHOP_TOTAL_SALES_BY_COUNTRY
} from "./Provider/Static/TotalSalesByCountryProvider";
import TotalSalesByDateProvider, { SHOP_TOTAL_SALES_BY_DATE } from "./Provider/Static/TotalSalesByDateProvider";
import TotalSalesByEventProvider, { SHOP_TOTAL_SALES_BY_EVENT } from "./Provider/Static/TotalSalesByEventProvider";
import TotalSalesByFunnelProvider, { SHOP_TOTAL_SALES_BY_FUNNEL } from "./Provider/Static/TotalSalesByFunnelProvider";
import TotalSalesByListProvider, { SHOP_TOTAL_SALES_BY_LIST } from "./Provider/Static/TotalSalesByListProvider";
import TotalSalesByTagProvider, { SHOP_TOTAL_SALES_BY_TAG } from "./Provider/Static/TotalSalesByTagProvider";
import "./style.less";

export * from "./types";

export { type IReport };
export default Report;

export type DynamicReportProps = {
  alwaysShowRunButton?: boolean;
  fields?: Data.FormField[];
  fieldStyle?: React.CSSProperties;
  id: string;
  getItemDetail?: ReportGetItemDetail;
  noGrid?: boolean;
  noRun?: boolean;
  onChange?: (reportId: string) => void;
  onLoaded?: () => void;
  getReport?: (id: string) => Promise<Data.Api.Report>;
  getChartData?: (id: string, parameters: GenericMap<any>) => Promise<object[]>;
  getGridData?: (id: string, parameters: GenericMap<any>) => Promise<object[]>;
  options?: OptionItem<string>[];
  settings?: ReportSettings;
  title?: ReportTitle;
  useUrlState?: boolean;
};

export const DynamicReport = React.forwardRef((props: DynamicReportProps, ref: React.RefObject<IReport>) => {
  const navigation = useNavigation();

  React.useEffect(() => {
    if (props.useUrlState) {
      const qs = parseQueryString();
      if (qs.reportId !== props.id) {
        navigation.setQuery({ reportId: props.id });
      }
    }
  }, [props.id, props.useUrlState]);

  const children = (data: ReportData) => (
    <Report
      ref={ref}
      alwaysShowRunButton={props.alwaysShowRunButton ?? false}
      fieldStyle={props.fieldStyle}
      getItemDetail={props.getItemDetail}
      noRun={props.noRun}
      onChange={props.onChange}
      options={props.options}
      settings={props.settings}
      rootId={props.id}
      title={props.title}
      useUrlState={props.useUrlState ?? false}
      {...data}
      gridConfig={props.noGrid ? null : data.gridConfig}
      getChartData={props.getChartData ?? data.getChartData}
      getGridData={props.getGridData ?? data.getGridData}
    />
  );

  // TODO: move out to admin
  if (props.id === CONVERSIONS_BY_TRIAL)
    return <ConversionsByTrialProvider onLoaded={props.onLoaded}>{children}</ConversionsByTrialProvider>;

  if (props.id === WEB_TRAFFIC_BY_URL_ID)
    return <TrafficByUrlProvider onLoaded={props.onLoaded}>{children}</TrafficByUrlProvider>;

  if (props.id === SHOP_TOTAL_SALES_BY_ACCESS_CARD)
    return <TotalSalesByAccessCardProvider onLoaded={props.onLoaded}>{children}</TotalSalesByAccessCardProvider>;

  if (props.id === SHOP_TOTAL_SALES_BY_CONTACT)
    return <TotalSalesByContactProvider onLoaded={props.onLoaded}>{children}</TotalSalesByContactProvider>;

  if (props.id === SHOP_TOTAL_SALES_BY_FUNNEL)
    return <TotalSalesByFunnelProvider onLoaded={props.onLoaded}>{children}</TotalSalesByFunnelProvider>;

  if (props.id === SHOP_TOTAL_SALES_BY_LIST)
    return <TotalSalesByListProvider onLoaded={props.onLoaded}>{children}</TotalSalesByListProvider>;

  if (props.id === SHOP_TOTAL_SALES_BY_TAG)
    return <TotalSalesByTagProvider onLoaded={props.onLoaded}>{children}</TotalSalesByTagProvider>;

  if (props.id === SHOP_TOTAL_SALES_BY_COUNTRY)
    return <TotalSalesByCountryProvider onLoaded={props.onLoaded}>{children}</TotalSalesByCountryProvider>;

  if (props.id === SHOP_TOTAL_SALES_BY_EVENT)
    return <TotalSalesByEventProvider onLoaded={props.onLoaded}>{children}</TotalSalesByEventProvider>;

  if (props.id === SHOP_TOTAL_SALES_BY_DATE)
    return <TotalSalesByDateProvider onLoaded={props.onLoaded}>{children}</TotalSalesByDateProvider>;

  return (
    <BackendProvider id={props.id} fields={props.fields} get={props.getReport} onLoaded={props.onLoaded}>
      {children}
    </BackendProvider>
  );
});
